import { MEGAPHONE_ICON_SIZE } from "./UsernameDisplaySizes";

// Explicit game-pixel square, same size as the megaphone badge: a percentage height on an <img> only
// resolves once it has intrinsic dimensions, and the 24px SVG box then overflows the 14px pill.
const ICON_SIZE = `calc(${MEGAPHONE_ICON_SIZE}px * var(--username-dom-scale, 1))`;

/**
 * A small raised-hand badge displayed next to the player's name when they raised their hand in a
 * meeting. It mirrors the behaviour of {@link UsernameMegaphoneDisplay}: a DOM element living inside
 * the username flex container, animated in and out. The icon is the same "hand-stop" glyph used by
 * RaiseHandIcon.svelte, so the feature looks identical wherever it appears.
 */
export class UsernameRaisedHandDisplay {
    public readonly element: HTMLImageElement;

    private shown = false;
    private animation?: Animation;

    constructor() {
        this.element = document.createElement("img");
        this.element.src = "/resources/icons/icon_raise_hand.svg";
        this.element.alt = "";
        this.element.draggable = false;
        this.element.setAttribute("aria-hidden", "true");
        this.element.style.display = "none";
        this.element.style.flex = "0 0 auto";
        this.element.style.width = ICON_SIZE;
        this.element.style.height = ICON_SIZE;
        this.element.style.marginLeft = `calc(-2px * var(--username-dom-scale, 1))`;
        this.element.style.opacity = "0";
        this.element.style.pointerEvents = "none";
        this.element.style.transformOrigin = "center";
        this.element.style.userSelect = "none";
    }

    public show(show = true, forceClose = false): void {
        if (this.shown === show && !forceClose) {
            return;
        }

        this.stopAnimation();
        this.shown = show;

        if (forceClose && !show) {
            this.element.style.display = "none";
            this.element.style.opacity = "0";
            this.element.style.transform = "";
            return;
        }

        if (show) {
            this.element.style.display = "";
            this.element.style.opacity = "0";
            this.animation = this.element.animate(
                [
                    { opacity: 0, transform: "translateY(calc(6px * var(--username-dom-scale, 1))) scale(0.17)" },
                    { opacity: 1, transform: "translateY(0) scale(1)" },
                ],
                {
                    duration: 350,
                    easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
                },
            );
        } else {
            this.animation = this.element.animate([{ opacity: 1 }, { opacity: 0 }], {
                duration: 350,
                easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
            });
        }

        this.animation.onfinish = () => {
            this.animation = undefined;
            this.element.style.opacity = show ? "1" : "0";
            this.element.style.transform = "";
            if (!show) {
                this.element.style.display = "none";
            }
        };
    }

    public destroy(): void {
        this.stopAnimation();
        this.element.remove();
    }

    public isShown(): boolean {
        return this.shown;
    }

    private stopAnimation(): void {
        this.animation?.cancel();
        this.animation = undefined;
    }
}
